/**
 * API Service for Notification Templates.
 * Templates are MERCHANT-SCOPED.
 *
 * Backend: Alaric.Services.Messaging - TemplatesController
 * Base route: /api/v1/templates
 */

import { Result } from "../../../models/Result";
import {
    TemplateDto,
    TemplateListItemDto,
    CreateTemplateRequest,
    UpdateTemplateRequest,
    TemplateSearchParams,
    TemplateStatus,
    NotificationChannel,
} from "../../../models/notifications";
import { messagingHttpService } from "./MessagingHttpService";

const BASE_PATH = "/api/v1/templates";

/**
 * Build query string from search parameters
 */
const buildQueryString = (params: TemplateSearchParams): string => {
    const queryParams: string[] = [`merchantId=${params.merchantId}`];

    if (params.eventCode) {
        queryParams.push(`eventCode=${encodeURIComponent(params.eventCode)}`);
    }
    if (params.channel !== undefined) {
        queryParams.push(`channel=${params.channel}`);
    }
    if (params.status !== undefined) {
        queryParams.push(`status=${params.status}`);
    }
    if (params.pageNumber) {
        queryParams.push(`pageNumber=${params.pageNumber}`);
    }
    if (params.pageSize) {
        queryParams.push(`pageSize=${params.pageSize}`);
    }

    return queryParams.join("&");
};

class TemplatesApiService {
    /**
     * Get all templates for a merchant.
     * @param merchantId The merchant ID.
     */
    getAll = async (merchantId: number): Promise<Result<TemplateListItemDto[]>> => {
        return messagingHttpService.get<TemplateListItemDto[]>(`${BASE_PATH}?merchantId=${merchantId}`);
    };

    /**
     * Search templates with filters.
     * @param params Search parameters including merchantId, eventCode, channel, status.
     */
    search = async (params: TemplateSearchParams): Promise<Result<TemplateListItemDto[]>> => {
        const queryString = buildQueryString(params);
        return messagingHttpService.get<TemplateListItemDto[]>(`${BASE_PATH}?${queryString}`);
    };

    /**
     * Get templates filtered by event code.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to filter by.
     */
    getByEventCode = async (merchantId: number, eventCode: string): Promise<Result<TemplateListItemDto[]>> => {
        return this.search({ merchantId, eventCode });
    };

    /**
     * Get templates filtered by channel.
     * @param merchantId The merchant ID.
     * @param channel The channel to filter by.
     */
    getByChannel = async (merchantId: number, channel: NotificationChannel): Promise<Result<TemplateListItemDto[]>> => {
        return this.search({ merchantId, channel });
    };

    /**
     * Get templates filtered by status.
     * @param merchantId The merchant ID.
     * @param status The status to filter by.
     */
    getByStatus = async (merchantId: number, status: TemplateStatus): Promise<Result<TemplateListItemDto[]>> => {
        return this.search({ merchantId, status });
    };

    /**
     * Get a specific template with all localizations.
     * @param merchantId The merchant ID.
     * @param templateId The template GUID.
     */
    getById = async (merchantId: number, templateId: string): Promise<Result<TemplateDto>> => {
        return messagingHttpService.get<TemplateDto>(`${BASE_PATH}/${templateId}?merchantId=${merchantId}`);
    };

    /**
     * Create a new template.
     * @param request The template to create.
     */
    create = async (request: CreateTemplateRequest): Promise<Result<string>> => {
        return messagingHttpService.post<string>(BASE_PATH, request);
    };

    /**
     * Update a template.
     * @param merchantId The merchant ID.
     * @param templateId The template GUID to update.
     * @param request The updated template data.
     */
    update = async (merchantId: number, templateId: string, request: UpdateTemplateRequest): Promise<Result<void>> => {
        return messagingHttpService.put<void>(`${BASE_PATH}/${templateId}?merchantId=${merchantId}`, request);
    };

    /**
     * Update template status only.
     * @param merchantId The merchant ID.
     * @param templateId The template GUID.
     * @param status The new status.
     * @param updatedBy Who is updating the template.
     */
    updateStatus = async (
        merchantId: number,
        templateId: string,
        status: TemplateStatus,
        updatedBy: string
    ): Promise<Result<void>> => {
        return this.update(merchantId, templateId, { status, updatedBy });
    };

    /**
     * Activate a template (set status to Active).
     * @param merchantId The merchant ID.
     * @param templateId The template GUID.
     * @param updatedBy Who is activating the template.
     */
    activate = async (merchantId: number, templateId: string, updatedBy: string): Promise<Result<void>> => {
        return this.updateStatus(merchantId, templateId, TemplateStatus.Active, updatedBy);
    };

    /**
     * Disable a template (set status to Disabled).
     * @param merchantId The merchant ID.
     * @param templateId The template GUID.
     * @param updatedBy Who is disabling the template.
     */
    disable = async (merchantId: number, templateId: string, updatedBy: string): Promise<Result<void>> => {
        return this.updateStatus(merchantId, templateId, TemplateStatus.Disabled, updatedBy);
    };

    /**
     * Soft-delete a template.
     * @param merchantId The merchant ID.
     * @param templateId The template GUID to delete.
     * @param deletedBy Who is deleting the template.
     */
    delete = async (merchantId: number, templateId: string, deletedBy: string): Promise<Result<void>> => {
        return messagingHttpService.delete<void>(
            `${BASE_PATH}/${templateId}?merchantId=${merchantId}&deletedBy=${encodeURIComponent(deletedBy)}`
        );
    };
}

const templatesApiService = new TemplatesApiService();

export { templatesApiService };
